import React from "react";
import { FileText, FileSignature, FileCheck2, Landmark, Scale, Ruler, AlertCircle, Building } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import EnquiryForm from "@/components/EnquiryForm";
import SEO from "@/components/SEO";
import { COVERAGE_OFFICES } from "@/lib/api";

const SECTIONS = [
  { icon: FileSignature, title: "Mutation", desc: "Updating revenue records after sale, inheritance or gift. We assist with application preparation and submission coordination." },
  { icon: FileCheck2, title: "ROR / Porcha", desc: "Record of Rights extract — application assistance and verification coordination." },
  { icon: Landmark, title: "Land Conversion", desc: "Agricultural-to-residential / commercial conversion application support, subject to applicable law." },
  { icon: FileText, title: "Deed Registry Assistance", desc: "Coordination for registration formalities and documentation." },
  { icon: Ruler, title: "Plot & Khatian Information", desc: "Record verification coordination and clear interpretation of record entries." },
  { icon: Scale, title: "Boundary Dispute Documentation", desc: "Preparing documentation and survey outputs to support dispute conversations." },
];

const CHECKLIST = [
  "Latest ROR / Porcha",
  "Chain of registered deeds",
  "Previous mutation records",
  "Property tax receipts",
  "Identity proof of applicant",
  "Site location sketch / map",
];

export default function Documentation() {
  return (
    <>
      <SEO
        title="Property Documentation & Land Revenue Assistance"
        description="Mutation, ROR / Porcha, land conversion, deed registration and plot & khatian record coordination in Kolkata and South 24 Parganas."
        path="/documentation"
        keywords="land mutation Kolkata, porcha assistance, record of rights West Bengal, land conversion South 24 Parganas, deed registration assistance"
      />
      <section className="bg-[#101827] text-white">
        <div className="ae-container pt-28 pb-16">
          <div className="ae-eyebrow mb-3">Documentation & Compliance</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold max-w-4xl">
            Property Documentation & Land Revenue Assistance
          </h1>
          <p className="text-white/80 mt-5 max-w-3xl leading-relaxed">
            Coordinated support for mutation, ROR, land conversion and allied revenue processes.
            Government outcomes remain with the competent authority — we help you get there with
            cleaner paperwork and clearer process.
          </p>
        </div>
      </section>

      <section className="ae-section">
        <div className="ae-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SECTIONS.map((s, i) => (
              <div key={i} className="ae-card p-6">
                <div className="w-11 h-11 rounded-lg bg-[#183B66] text-white flex items-center justify-center mb-4"><s.icon className="w-5 h-5" /></div>
                <h3 className="font-semibold text-[#101827] text-lg mb-1.5">{s.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ae-section bg-white border-t border-[#E2E8F0]">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="ae-eyebrow mb-3">Offices We Work With</div>
            <h2 className="font-display text-3xl font-semibold text-[#101827] leading-tight">Field knowledge of local revenue offices.</h2>
            <p className="mt-4 text-[#475569] leading-relaxed">
              Our documentation practice is grounded in on-the-ground familiarity with the land revenue offices that handle most of our mandates. We know which forms, enclosures and sequence help your file move cleanly.
            </p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {COVERAGE_OFFICES.map((o, i) => (
              <div key={i} className="ae-card p-5 flex flex-col gap-2">
                <div className="w-10 h-10 rounded-lg bg-[#101827] text-[#C5A46D] flex items-center justify-center"><Building className="w-5 h-5" /></div>
                <div className="font-semibold text-[#101827] text-sm leading-snug">{o}</div>
                <div className="text-xs text-[#64748B]">Mutation · ROR · Conversion · Record coordination</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ae-section bg-[#F4F6F8]">
        <div className="ae-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <SectionHeader eyebrow="Documents" title="Typical document checklist" subtitle="Exact requirements vary with your case. Share your matter and we'll tailor the list." />
            <ul className="grid sm:grid-cols-2 gap-3">
              {CHECKLIST.map((c, i) => (
                <li key={i} className="ae-card p-4 flex items-start gap-3 text-sm text-[#1F2937]"><FileText className="w-5 h-5 text-[#C5A46D] shrink-0 mt-0.5" /> {c}</li>
              ))}
            </ul>

            <div className="ae-card p-6 mt-8 border-l-4 border-l-[#C5A46D] bg-white">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-[#C5A46D] shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-[#101827] mb-1">Important disclaimer</h4>
                  <p className="text-[#475569] text-sm leading-relaxed">
                    Government decisions, approvals, statutory records and legal outcomes remain subject to
                    the competent authority and applicable law. Apurba Estate does not guarantee approval
                    or outcome. Legal opinions and court representation require appropriately qualified
                    professionals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="ae-card p-6 sticky top-28">
              <EnquiryForm defaultService="documentation_request" sourcePage="documentation" title="Discuss your land documentation requirement" />
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
